/**
 * Camera connectivity check (ENG-2457): the shapes actuate_admin returns for
 * the connector's own per-camera report (`GET site-connectivity/{site}/`) and
 * for a full camera test (`camera-dry-run/…`). Both use the same result shape,
 * so one panel renders either. The catalog of reasons, their customer-facing
 * wording and who owns the fix live in admin; this file only names the codes.
 */

/**
 * How the site gets its pictures. Each family checks different steps:
 * direct cameras/NVRs, a VMS, a cloud service, email (SMTP), or events pushed
 * by a partner. Anything admin doesn't know reads as `direct`.
 */
export type IntegrationFamily = "direct" | "vms" | "cloud" | "smtp" | "events";

/** How the connector reaches the site's network. */
export type ConnectionPath = "default_path" | "wireguard" | "vpn";

/**
 * The WireGuard tunnel or VPN (network path) a camera goes through, by id and
 * the name the customer sees on their tunnels page. Older admin builds don't
 * send it.
 */
export interface ConnectionPathRef {
  kind: "wireguard" | "vpn" | string;
  id: number;
  name: string | null;
}

/**
 * A tunnel_down / vpn_down group's path, plus (WireGuard only) how many of
 * the site's other tunnels are up — null for a VPN.
 */
export interface ConnectionPathContext extends ConnectionPathRef {
  others_up: number | null;
  others_total: number | null;
}

/** Steps checked before the family's own steps, by connection. */
export const CONNECTION_STEPS: Record<ConnectionPath, string[]> = {
  default_path: [],
  wireguard: ["tunnel_up", "address_in_tunnel"],
  vpn: ["vpn_up", "address_routed"],
};

/** Each family's steps, in the order a camera goes through them. */
export const FAMILY_STEPS: Record<IntegrationFamily, string[]> = {
  direct: ["resolve", "port", "login", "stream_open", "first_frame", "quality"],
  vms: [
    "vms_reachable",
    "vms_login",
    "camera_in_vms",
    "stream_open",
    "first_frame",
    "quality",
  ],
  cloud: [
    "cloud_login",
    "camera_listed",
    "stream_open",
    "first_frame",
    "quality",
  ],
  smtp: [
    "email_received",
    "image_attached",
    "camera_matched",
    "image_readable",
  ],
  events: [
    "account_linked",
    "events_arriving",
    "clip_downloads",
    "frames_readable",
  ],
};

/**
 * A step id. Not a fixed list: each camera's `steps[]` comes back ordered and
 * family-specific, and the UI renders whatever ids arrive.
 */
export type StepId = string;

export type StepState = "pass" | "fail" | "not_reached" | "unknown";

export interface CameraStep {
  id: StepId;
  state: StepState;
}

export type ReasonCode =
  | "dns_failed"
  | "unreachable"
  | "port_closed"
  | "auth"
  | "site_login_failed"
  | "reset_by_peer"
  | "not_found"
  | "timeout"
  | "no_frames_after_open"
  | "codec"
  | "not_in_vms"
  | "low_resolution"
  | "high_resolution"
  | "duplicate_source"
  | "never_connected"
  | "connected_no_frames"
  | "tunnel_down"
  | "address_outside_tunnel"
  | "vpn_down"
  | "address_not_routed"
  | "vms_unreachable"
  | "camera_not_listed"
  | "cloud_login_failed"
  | "cloud_relay_busy"
  | "no_email_received"
  | "no_image_attached"
  | "filename_unmatched"
  | "image_unreadable"
  | "account_not_linked"
  | "no_events"
  | "clip_download_failed"
  | "unknown";

export type FixOwner = "customer" | "actuate" | "vms";

/** One entry of admin's reason catalog. */
export interface ConnectivityFinding {
  code: ReasonCode | string;
  step?: StepId | string | null;
  title: string;
  body: string;
  steps: string[];
  owner: FixOwner | string;
}

export type CameraConnectivityStatus =
  | "streaming"
  | "not_streaming"
  | "unknown"
  /** Reachable, but video is only opened on first motion (motion-gated Genetec). */
  | "unproven"
  /** Email (SMTP) sites: no email from this camera yet. */
  | "waiting";

export type UrlKind = "direct" | "gateway" | "playback" | "rtsp" | "cloud";

/**
 * What a test captured from one camera (ENG-2457 / ENG-2470): a frame and a
 * short clip, both presigned links that expire at `media_expires_at`. Absent
 * from older admin builds and from the connector's own report.
 */
export interface CameraMedia {
  snapshot_url?: string | null;
  /** About 5 seconds of MP4, presigned for 10 minutes. */
  clip_url?: string | null;
  clip_seconds?: number | null;
  /** We converted the camera's video to H.264 so a browser can play it. */
  clip_transcoded?: boolean | null;
  media_expires_at?: string | null;
}

export interface CameraConnectivity extends CameraMedia {
  camera_id: number;
  camera_name: string;
  status: CameraConnectivityStatus;
  steps: CameraStep[];
  reason: ReasonCode | string | null;
  /** Free text from the connector, shown as evidence. */
  detail: string | null;
  width: number | null;
  height: number | null;
  codec: string | null;
  ms_to_first_frame: number | null;
  /** Frames per second, when the test measured it. */
  fps?: number | null;
  url_kind: UrlKind | string | null;
  /** The tunnel / VPN this camera goes through; absent from older admin builds. */
  connection?: ConnectionPathRef | null;
  /**
   * Where "Open device" goes (ENG-2507): "server" = through the site's NVR /
   * recorder (the site button), "camera" or missing = the camera itself.
   */
  open_device_target?: "server" | "camera" | null;
}

export interface ConnectivityGroup {
  stopped_at: StepId | string | null;
  reason: ReasonCode | string | null;
  count: number;
  camera_ids: number[];
  /**
   * tunnel_down / vpn_down groups are per tunnel; this names it. admin
   * already writes the name into `finding`, so this is for display only.
   */
  context?: ConnectionPathContext | null;
  finding: ConnectivityFinding | null;
}

export interface ConnectivitySummary {
  total: number;
  streaming: number;
  not_streaming: number;
  unknown: number;
  unproven: number;
  /** Email (SMTP) sites: cameras with no email yet. */
  waiting?: number;
}

export type ConnectivityRunStatus =
  | "done"
  | "pending"
  | "running"
  | "failed"
  | "timed_out"
  | "unavailable"
  /** Email (SMTP) sites before the first email arrives. */
  | "waiting";

export interface ConnectivityResult {
  site_id: number;
  /** Optional until admin sends it; absent reads as `direct`. */
  family?: IntegrationFamily | string | null;
  connection?: ConnectionPath | string | null;
  source: "connector_status" | "dry_run";
  run_id: string | null;
  status: ConnectivityRunStatus;
  checked_at: string | null;
  settings_source: "preview" | "deployed" | null;
  mode: "capped" | "burst" | null;
  summary: ConnectivitySummary;
  groups: ConnectivityGroup[];
  cameras: CameraConnectivity[];
  /** Present when a run could not finish. */
  error?: string | null;
  /** admin's plain-words note: why a test failed or isn't available; else null. */
  detail?: string | null;
}

export interface StartDryRunRequest {
  site_id: number;
  camera_ids?: number[];
  settings_source?: "preview" | "deployed";
  mode?: "capped" | "burst";
}

export interface StartDryRunResponse {
  run_id: string;
  status: ConnectivityRunStatus;
}

/**
 * What a call came back with, in the terms the UI needs. Everything the
 * backend can say "no" with is a value here, not a thrown error, so each
 * surface can explain it in plain words.
 */
export type ApiOutcome<T> =
  | { kind: "ok"; data: T }
  /** The endpoint, or the dry run behind it, isn't switched on yet (404/405/503). */
  | { kind: "unavailable" }
  /** A test is already running for this site (409). */
  | { kind: "busy"; runId: string | null }
  /** User-triggered tests are limited to one per site per hour (429). */
  | { kind: "rate_limited"; retryAt: string | null }
  /** Our test capacity is full right now (429 `cluster_busy`). */
  | { kind: "cluster_busy"; retryAt: string | null }
  /** The full test doesn't cover this kind of site, e.g. fleet sites (400 `not_supported`). */
  | { kind: "not_supported" }
  /** The request can't run as asked (400 `no_cameras`, `too_many_cameras`). */
  | { kind: "rejected"; code: string; message: string }
  /** Nothing to show yet, e.g. no test has ever run for this site. */
  | { kind: "not_found" }
  | { kind: "forbidden"; message: string }
  | { kind: "error"; message: string };

/** Statuses after which a run will not change any more. */
export const FINISHED_RUN_STATUSES: ReadonlySet<ConnectivityRunStatus> =
  new Set(["done", "failed", "timed_out", "unavailable"]);
